import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, mergeClasses, Tooltip } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Home20Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  Search20Regular,
  PersonBoard20Regular,
  Add20Regular,
  Info16Regular,
  CheckmarkCircle20Regular,
  ArrowUndo20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { CleanupContactPreview } from "../components/CleanupContactPreview";
import { CleanupContactEditPanel } from "../components/CleanupContactEditPanel";
import { ContactRefField } from "../components/relationsInline/ContactRefField";
import { AddressAutocomplete } from "../components/forms/widgets/AddressAutocomplete";
import { LabeledSelect } from "../components/forms/formPrimitives";
import { apiUrl } from "../utils/api";
import { setReloadWarningSuppressed } from "../utils/reloadWarningSuppress";
import { refDisplayName, type ContactRef } from "../components/relationsInline/types";
import { VerantwortlichkeitInput, type VerantwortlichkeitValue } from "../components/verantwortlichkeit";
import {
  listHaushalte,
  listHaushalteTeams,
  listRelationClusters,
  listNameCandidates,
  listDismissedCandidates,
  dismissCandidate,
  restoreCandidate,
  fetchHaushalt,
  saveHaushalt,
  HOUSEHOLD_ROLES,
  genderKey,
  deriveRelationLabel,
  reverseRelationLabel,
  rolesFromRelationLabel,
  roleKeyFromLabel,
  linkKind,
  composeKinship,
  type CleanupStatus,
  type CandidateSource,
  type DismissKind,
  type DismissedRow,
  type RelationClusterRow,
  type NameCandidateRow,
  type CandidatePerson,
  type HouseholdListRow,
  type HouseholdDetail,
  type HouseholdMember,
  type HouseholdRoleKey,
  type HouseholdMemberInput,
  type HouseholdRelationInput,
  type HouseholdPersonRelInput,
  type HouseholdFirmaRelInput,
} from "../services/haushalteCleanupService";
import { useHouseholdRoles, useHouseholdRelationLabels } from "../services/useHouseholdLists";
import { RELATIONSHIP_FREE } from "../utils/householdRole";
import RelationClusterDetail from "./RelationClusterDetail";

const PAGE = 30;

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  Kunde: { bg: "#e7f3ec", fg: "#1c7a44" },
  Interessent: { bg: "#fdf2e3", fg: "#9a6212" },
  Altkunde: { bg: "#f0eef5", fg: "#5b5470" },
  "Verantwortung fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  // Alter Name desselben Status (bis 29.09.2026).
  "Mensch fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  Haushaltsmitglied: { bg: "#e4f3f1", fg: "#13675a" },
};

const fmtDate = (d?: string | null): string => {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("de-DE");
};

/** Alter in vollen Jahren aus einem Geburtsdatum (ISO/Datums-String); null wenn
 *  nicht parsebar oder unplausibel. */
const ageFromBirthDate = (d?: string | null): number | null => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dt.getFullYear();
  const m = now.getMonth() - dt.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dt.getDate())) age--;
  return age >= 0 && age <= 150 ? age : null;
};

const memberName = (m: { name: string | null; firstName: string | null }): string =>
  [m.name, m.firstName].filter((x) => x && x.trim()).join(", ") || "—";

const addrLine = (a: { strasse: string | null; plz: string | null; ort: string | null }): string =>
  [a.strasse, [a.plz, a.ort].filter(Boolean).join(" ")].filter((x) => x && String(x).trim()).join(", ");

/** Anzeigename einer Person aus den Kandidatenlisten. */
const persName = (p: CandidatePerson): string =>
  [p.lastname, p.firstName].filter((x) => x && x.trim()).join(", ") || `#${p.contactId}`;

/**
 * „Bisherige Infos" einer Person: Kundennummer, Geburtsdatum, Wohnort — das, was der
 * Sachbearbeiter zum Einordnen braucht, ohne die Karte aufzumachen.
 */
const persInfo = (p: CandidatePerson): string => {
  const bits: string[] = [];
  if (p.kundennummer) bits.push(`Nr. ${p.kundennummer}`);
  if (p.birthDate) bits.push(`* ${fmtDate(p.birthDate)}`);
  const a = p.address;
  const ort = [a?.postalCode, a?.city].filter((x) => x && String(x).trim()).join(" ");
  if (ort) bits.push(ort);
  if (p.householdRole) bits.push(p.householdRole);
  return bits.length ? `${persName(p)} (${bits.join(", ")})` : persName(p);
};

/** Mitglieder einer Namensgruppe kompakt, mit Kappung bei vielen Personen. */
const memberSummary = (r: NameCandidateRow): string => {
  const shown = r.members.slice(0, 4).map(persInfo);
  const rest = r.members.length - shown.length;
  return [shown.join("  •  "), rest > 0 ? `+ ${rest} weitere` : ""].filter(Boolean).join("  •  ");
};

/** Bereits vorhandene Beziehungs-Labels innerhalb einer Namensgruppe (dedupliziert). */
const relationSummary = (r: NameCandidateRow): string =>
  Array.from(
    new Set((r.relations ?? []).map((x) => x.relationType).filter((x): x is string => !!x)),
  ).join(", ");

const roleLabelOf = (key: HouseholdRoleKey | null): string =>
  HOUSEHOLD_ROLES.find((r) => r.key === key)?.label ?? "—";

/** Geschlecht (Symbol + Label + Farbe) aus rohem gender-Wert. */
const GENDER_VIS: Record<string, { sym: string; color: string }> = {
  Männlich: { sym: "♂", color: "#2b6cb0" },
  Weiblich: { sym: "♀", color: "#b83280" },
  Divers: { sym: "⚧", color: "#6b5a66" },
};

/** Per-member form state (key = member key string). */
type MemberForm = { role: HouseholdRoleKey | null; exclude: boolean };
/** Manuell hinzugefügtes Mitglied (über Personensuche/Eintippen). */
type AddedMember = { key: string; ref: ContactRef };

/**
 * Ein weiterer Person-Beziehungs-Eintrag (beidseitig angezeigt). `label` = „was die
 * referenzierte Person für das Mitglied ist"; `reverseLabel` trägt bei Freitext die
 * Gegenrichtung („was das Mitglied für die Person ist").
 */
type PersonRelEntry = {
  id: string;
  ref: ContactRef | null;
  label: string;
  reverseLabel?: string;
  sameHousehold: boolean;
};
/** Eine Firmen-Verbindung (Firma + Verantwortlichkeit, wie AP). */
type FirmaEntry = { id: string; firmaRef: ContactRef | null; verant: VerantwortlichkeitValue };

const blankVerant = (): VerantwortlichkeitValue => ({ isExtern: false, roles: [], externalCompany: null });

let _addSeq = 0;
let _entSeq = 0;
const uid = (): string => `e${(_entSeq++).toString(36)}`;

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "transparent",
    border: "1px solid transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    borderRadius: "6px",
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "24px",
    paddingRight: "24px",
    borderRadius: "10px",
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "38px",
    height: "38px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    borderRadius: "999px",
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.3rem, 3vw, 1.7rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.88rem" },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "10px", rowGap: "10px" },
  headerCount: {
    fontSize: "0.82rem",
    fontWeight: 700,
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    borderRadius: "999px",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    whiteSpace: "nowrap",
  },
  segWrap: { display: "inline-flex", border: `1px solid ${BRAND.borderDashed}`, borderRadius: "8px", overflow: "hidden" },
  segBtn: {
    appearance: "none",
    border: "none",
    backgroundColor: BRAND.white,
    color: BRAND.muted,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontSize: "0.85rem",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
  },
  segActive: { backgroundColor: BRAND.purple, color: BRAND.white },
  cardRow: { display: "flex", alignItems: "stretch", columnGap: "8px", marginBottom: "10px" },
  sideBtn: {
    appearance: "none",
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "3px",
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "12px",
    backgroundColor: BRAND.white,
    color: BRAND.muted,
    paddingLeft: "10px",
    paddingRight: "10px",
    width: "94px",
    flexShrink: 0,
    fontSize: "0.68rem",
    lineHeight: "1.15",
    textAlign: "center",
    fontFamily: BRAND.fontPrimary,
    cursor: "pointer",
    ":hover": { ...shorthands.borderColor(BRAND.purple), color: BRAND.purple },
  },
  undoBar: {
    position: "fixed",
    left: "50%",
    bottom: "28px",
    transform: "translateX(-50%)",
    zIndex: 100003,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "16px",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    paddingTop: "11px",
    paddingBottom: "11px",
    paddingLeft: "18px",
    paddingRight: "18px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(45,20,43,0.28)",
    fontSize: "0.85rem",
    fontFamily: BRAND.fontPrimary,
    maxWidth: "min(460px, calc(100vw - 32px))",
  },
  undoBtn: {
    appearance: "none",
    border: "none",
    backgroundColor: "transparent",
    color: BRAND.gold,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "0.85rem",
    fontFamily: BRAND.fontPrimary,
    textDecorationLine: "underline",
    padding: 0,
    whiteSpace: "nowrap",
  },
  searchBox: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.white,
    flexGrow: 1,
    minWidth: "200px",
  },
  searchInput: {
    border: "none",
    outlineStyle: "none",
    paddingTop: "9px",
    paddingBottom: "9px",
    fontSize: "0.9rem",
    flexGrow: 1,
    backgroundColor: "transparent",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
  },
  scrollArea: { maxHeight: "calc(100vh - 290px)", overflowY: "auto", paddingRight: "4px" },
  card: {
    appearance: "none",
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    boxShadow: "0 4px 12px rgba(45,20,43,0.05)",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  cardIcon: {
    width: "42px",
    height: "42px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    borderRadius: "999px",
  },
  cardBody: { display: "flex", flexDirection: "column", rowGap: "3px", flexGrow: 1, minWidth: 0 },
  cardTitle: { fontFamily: BRAND.fontAccent, fontSize: "1.05rem", color: BRAND.ink, whiteSpace: "normal" },
  cardDesc: { color: BRAND.muted, fontSize: "0.82rem", whiteSpace: "normal" },
  respChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    flexShrink: 0,
    fontSize: "0.74rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: "#eef0f6",
    borderRadius: "999px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "9px",
    paddingRight: "10px",
    maxWidth: "180px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  countPill: {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    borderRadius: "999px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "9px",
    paddingRight: "9px",
    flexShrink: 0,
  },
  /** Aktuelles (zu grobes) Beziehungs-Label auf einer Kandidaten-Karte. */
  labelChip: {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: "#8a5a00",
    backgroundColor: "#fdf3e0",
    borderRadius: "999px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "9px",
    paddingRight: "9px",
    flexShrink: 0,
    maxWidth: "220px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  /** Erklärzeile unter den Filtern (je Kandidaten-Quelle). */
  sourceHint: { color: BRAND.muted, fontSize: "0.8rem", flexBasis: "100%", marginTop: "-2px" },
  /** Warnung, dass Speichern Mitglieder aus einem anderen Haushalt übernimmt. */
  warnBox: {
    border: "1px solid #e6c88a",
    backgroundColor: "#fdf6e7",
    borderRadius: "8px",
    color: "#6b4e12",
    fontSize: "0.86rem",
    lineHeight: 1.45,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    marginBottom: "12px",
  },
  empty: { color: BRAND.muted, fontSize: "0.9rem", paddingTop: "20px", textAlign: "center" },
  // detail
  detailHead: {
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "12px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  detailAddr: { fontFamily: BRAND.fontAccent, fontSize: "1.2rem", color: BRAND.ink },
  detailSub: { color: BRAND.muted, fontSize: "0.84rem" },
  memberCard: {
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  memberCardExcluded: { border: `2px solid ${BRAND.purple}` },
  memberTop: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap" },
  memberNo: { fontFamily: BRAND.fontAccent, fontSize: "1.05rem", fontWeight: 700, color: BRAND.purple },
  memberName: { fontFamily: BRAND.fontAccent, fontSize: "1.05rem", color: BRAND.ink },
  genderPill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.74rem",
    fontWeight: 600,
    borderRadius: "999px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "10px",
    backgroundColor: "#f3f1f7",
  },
  statusChip: { fontSize: "0.72rem", fontWeight: 600, borderRadius: "999px", paddingTop: "2px", paddingBottom: "2px", paddingLeft: "9px", paddingRight: "9px" },
  metaLine: { color: BRAND.muted, fontSize: "0.8rem", display: "flex", columnGap: "14px", flexWrap: "wrap" },
  expanderBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    border: "none",
    backgroundColor: "transparent",
    color: BRAND.purple,
    fontSize: "0.82rem",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    paddingLeft: 0,
  },
  roleRow: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap", paddingTop: "4px" },
  roleLabel: { fontSize: "0.84rem", fontWeight: 600, color: BRAND.ink },
  select: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: "8px",
    border: `1px solid ${BRAND.borderDashed}`,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    minWidth: "170px",
  },
  selectSm: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.84rem",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: "7px",
    border: `1px solid ${BRAND.borderDashed}`,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    minWidth: "150px",
  },
  freeInput: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: "8px",
    border: `1px solid ${BRAND.borderDashed}`,
    minWidth: "170px",
  },
  /** Freitext-Beziehung: beide Richtungen untereinander, eingerückt unter dem Select. */
  freeRelBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    marginTop: "6px",
    marginLeft: "8px",
    paddingLeft: "10px",
    borderLeft: `2px solid ${BRAND.borderDashed}`,
    flexBasis: "100%",
  },
  freeRelRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  excludeRow: { display: "inline-flex", alignItems: "center", columnGap: "6px", fontSize: "0.82rem", color: BRAND.muted, cursor: "pointer" },
  relCardBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    backgroundColor: "#faf8fb",
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  relCardTitle: { fontSize: "0.78rem", fontWeight: 600, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.04em" },
  relCardRow: { display: "flex", alignItems: "center", columnGap: "8px", flexWrap: "wrap", fontSize: "0.84rem", color: BRAND.ink },
  relCardName: { minWidth: "150px" },
  addBox: {
    backgroundColor: BRAND.white,
    border: `1px dashed ${BRAND.purple}`,
    borderRadius: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  addTitle: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink, display: "flex", alignItems: "center", columnGap: "6px" },
  addBtn: {
    alignSelf: "flex-start",
    appearance: "none",
    border: "none",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "16px",
    paddingRight: "16px",
    borderRadius: "8px",
    cursor: "pointer",
    ":hover": { backgroundColor: BRAND.ink },
  },
  addBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  // overview table
  tableBox: {
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  tableTitle: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink },
  tableHint: { fontSize: "0.8rem", color: BRAND.muted },
  connMember: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    paddingTop: "10px",
    paddingBottom: "10px",
    borderTop: `1px solid #f0ecf3`,
  },
  connMemberName: { fontWeight: 600, fontSize: "0.9rem", color: BRAND.ink },
  connExisting: { fontSize: "0.83rem", color: BRAND.ink, display: "flex", flexDirection: "column", rowGap: "2px", paddingLeft: "4px" },
  yesNoWrap: { display: "inline-flex", columnGap: "6px" },
  yesNoBtn: {
    appearance: "none",
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "8px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontSize: "0.85rem",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
  },
  yesNoActive: { backgroundColor: BRAND.purple, color: BRAND.white, ...shorthands.borderColor(BRAND.purple) },
  qRow: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap", paddingTop: "4px", paddingBottom: "4px" },
  adderBox: { display: "flex", flexDirection: "column", rowGap: "10px", paddingTop: "6px", paddingLeft: "8px", borderLeft: `2px solid #f0e6f2` },
  adderEntry: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    backgroundColor: "#faf8fb",
    border: `1px solid ${BRAND.borderDashed}`,
    borderRadius: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  ghostBtn: {
    alignSelf: "flex-start",
    appearance: "none",
    border: `1px dashed ${BRAND.purple}`,
    backgroundColor: "transparent",
    color: BRAND.purple,
    fontSize: "0.84rem",
    fontWeight: 600,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "14px",
    paddingRight: "14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" },
  th: { textAlign: "left", color: BRAND.muted, fontWeight: 600, fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: "6px", paddingBottom: "6px", borderBottom: `1px solid ${BRAND.borderDashed}` },
  td: { paddingTop: "8px", paddingBottom: "8px", borderBottom: `1px solid #f0ecf3`, color: BRAND.ink },
  rowAnchor: { backgroundColor: "#f0e6f2" },
  rowClickable: { cursor: "pointer", ":hover": { backgroundColor: "#faf8fb" } },
  anchorTag: { fontSize: "0.7rem", fontWeight: 700, color: BRAND.purple, marginLeft: "6px" },
  saveBar: { display: "flex", alignItems: "center", columnGap: "12px", flexWrap: "wrap", paddingTop: "6px" },
  saveBtn: {
    appearance: "none",
    border: "none",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "22px",
    paddingRight: "22px",
    borderRadius: "8px",
    cursor: "pointer",
    ":hover": { backgroundColor: BRAND.ink },
  },
  saveBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  errText: { color: "#b3261e", fontSize: "0.85rem" },
  okText: { color: "#1c7a44", fontSize: "0.85rem", fontWeight: 600 },
  editPanelWrap: { ...shorthands.padding("4px", "0") },
});

/** Eine vereinheitlichte Mitglieds-Sicht (bestehender Kandidat ODER hinzugefügt). */
type UMember = { key: string; existing?: HouseholdMember; added?: AddedMember };

type AndererHHValue = {
  addrKey: string;
  nameKey: string;
  label: string;
  strasse: string;
  plz: string;
  ort: string;
};

/** Suche + Auswahl eines bestehenden Haushalts (für „gehört zu einem anderen Haushalt"). */
const HaushaltPicker: React.FC<{
  value: AndererHHValue | null;
  onChange: (v: AndererHHValue | null) => void;
  excludeAddrKey?: string;
}> = ({ value, onChange, excludeAddrKey }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<HouseholdListRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      Promise.all([
        listHaushalte("open", undefined, term, 8, 0).catch(() => ({ rows: [] as HouseholdListRow[], total: 0 })),
        listHaushalte("bereinigt", undefined, term, 8, 0).catch(() => ({ rows: [] as HouseholdListRow[], total: 0 })),
      ])
        .then(([a, b]) => {
          if (cancelled) return;
          const seen = new Set<string>();
          const merged: HouseholdListRow[] = [];
          for (const r of [...a.rows, ...b.rows]) {
            const k = `${r.addrKey}|${r.nameKey}`;
            if (seen.has(k) || r.addrKey === excludeAddrKey) continue;
            seen.add(k);
            merged.push(r);
          }
          setResults(merged.slice(0, 12));
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, excludeAddrKey]);

  const rowLabel = (r: HouseholdListRow) => {
    const names = (r.names ?? []).filter(Boolean).join(", ");
    const addr = [r.strasse, [r.plz, r.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return { names: names || "(Haushalt)", addr };
  };

  if (value) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: `1px solid ${BRAND.borderDashed}`, borderRadius: 8, background: BRAND.white }}>
        <span style={{ fontWeight: 600, color: BRAND.ink }}>{value.label}</span>
        <span style={{ color: BRAND.muted, fontSize: "0.83rem" }}>
          {[value.strasse, [value.plz, value.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          style={{ border: "none", background: "transparent", color: BRAND.purple, cursor: "pointer", fontSize: "0.83rem", fontWeight: 600 }}
        >
          ändern
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <input
        style={{
          padding: "8px 10px",
          border: `1px solid ${BRAND.borderDashed}`,
          borderRadius: 8,
          fontFamily: BRAND.fontPrimary,
          fontSize: "0.9rem",
        }}
        placeholder="Haushalt suchen (Name oder Ort) …"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {loading && <span style={{ color: BRAND.muted, fontSize: "0.83rem" }}>Suche …</span>}
      {!loading && q.trim() && results.length === 0 && (
        <span style={{ color: BRAND.muted, fontSize: "0.83rem" }}>Kein Haushalt gefunden.</span>
      )}
      {results.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${BRAND.borderDashed}`, borderRadius: 8, overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
          {results.map((r) => {
            const { names, addr } = rowLabel(r);
            return (
              <button
                key={`${r.addrKey}|${r.nameKey}`}
                type="button"
                onClick={() =>
                  onChange({
                    addrKey: r.addrKey,
                    nameKey: r.nameKey,
                    label: names,
                    strasse: r.strasse ?? "",
                    plz: r.plz ?? "",
                    ort: r.ort ?? "",
                  })
                }
                style={{
                  textAlign: "left",
                  border: "none",
                  borderBottom: `1px solid ${BRAND.bg}`,
                  background: BRAND.white,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontFamily: BRAND.fontPrimary,
                }}
              >
                <div style={{ fontWeight: 600, color: BRAND.ink, fontSize: "0.9rem" }}>{names}</div>
                <div style={{ color: BRAND.muted, fontSize: "0.8rem" }}>
                  {addr}
                  {r.memberCount ? ` · ${r.memberCount} Mitglieder` : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HaushalteCleanupPage: React.FC = () => {
  const s = useStyles();
  const goBack = useSmartBack("/datenbereinigung");

  // Konfigurierbare Listen (Settings) mit statischem Fallback.
  const householdRoles = useHouseholdRoles();
  const relationLabels = useHouseholdRelationLabels();
  const [status, setStatus] = useState<CleanupStatus>("open");
  const [teamId, setTeamId] = useState<string>("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [rows, setRows] = useState<HouseholdListRow[]>([]);
  // Kandidaten-Quelle: „addr" = Anschriften-Liste (Original), dazu zwei Quellen für
  // Altbestand, den HouseholdCandidate nie erfasst hat.
  const [source, setSource] = useState<CandidateSource>("addr");
  // Worklist „unklare Beziehung", je Cluster gebündelt (transitive Hülle über verbundene
  // Haushalte — löst die Doppelung „A bereinigt, B bleibt").
  const [clusters, setClusters] = useState<RelationClusterRow[]>([]);
  // Offener Cluster: die beteiligten Kontakt-IDs (Einstieg ins Cluster-Detail).
  const [clusterSel, setClusterSel] = useState<number[] | null>(null);
  // Woher die Cluster-Detailansicht geöffnet wurde — Grundlage für „als erledigt
  // markieren" dort unten (dieselbe Ausblend-Mechanik wie „keine Bereinigung" hier).
  const [clusterDismiss, setClusterDismiss] = useState<{
    kind: DismissKind;
    refKey: string;
    label: string;
  } | null>(null);
  const [nameRows, setNameRows] = useState<NameCandidateRow[]>([]);
  // Default an: ohne den Filter dominieren namensgleiche Zufallstreffer.
  const [nameWithRelation, setNameWithRelation] = useState(true);
  // „keine Bereinigung nötig": ausgeblendete Kandidaten (name/relation) + Undo.
  const [showDismissed, setShowDismissed] = useState(false);
  const [dismissedRows, setDismissedRows] = useState<DismissedRow[]>([]);
  const [undo, setUndo] = useState<{ kind: DismissKind; refKey: string; label: string } | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const [selected, setSelected] = useState<
    { addrKey: string; nameKey: string } | { anchorId: number } | null
  >(null);
  const [detail, setDetail] = useState<HouseholdDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [forms, setForms] = useState<Record<string, MemberForm>>({});
  const [added, setAdded] = useState<AddedMember[]>([]);
  const [pairLabels, setPairLabels] = useState<Record<string, string>>({});
  // Freitext-Gegenrichtung je Paar („was der FRÜHERE für den SPÄTEREN ist").
  // Nur bei „Sonstiges (Freitext)" gesetzt — Katalog-Labels werden gerechnet.
  const [pairRev, setPairRev] = useState<Record<string, string>>({});
  // „Sonstiges (Freitext)" je Beziehungs-Zeile — nötig, solange beide Texte noch
  // leer sind (dann verrät das Label den Modus nicht).
  const [freeMode, setFreeMode] = useState<Record<string, boolean>>({});
  const [anchorKey, setAnchorKey] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState<ContactRef | null>(null);
  const [infoOpen, setInfoOpen] = useState<Record<string, boolean>>({});
  // „Bisherige Informationen" je gewählter Person in den Beziehungs-Addern (keyed by entry-id).
  const [relInfoOpen, setRelInfoOpen] = useState<Record<string, boolean>>({});
  const [relOpen, setRelOpen] = useState<Record<string, boolean>>({});
  // Verbindungen / Nicht-Mitglied / Kundenverbindung
  const [personRels, setPersonRels] = useState<Record<string, PersonRelEntry[]>>({});
  const [firmaEntries, setFirmaEntries] = useState<Record<string, FirmaEntry[]>>({});
  const [mensch, setMensch] = useState<Record<string, boolean | null>>({});
  // „Mensch im Unternehmen" (Status-Link) — separat von „Mensch fürs Unternehmen" (AP-Flow).
  const [menschImUnternehmen, setMenschImUnternehmen] = useState<Record<string, boolean | null>>({});
  // „Mensch im Unternehmen": Pflicht-Firmenauswahl (für welche Firma).
  const [menschImUntFirma, setMenschImUntFirma] = useState<Record<string, ContactRef | null>>({});
  // Nicht-Mitglied-Fragen
  const [frageBez, setFrageBez] = useState<Record<string, boolean | null>>({});
  const [frageHH, setFrageHH] = useState<Record<string, boolean | null>>({});
  // Nicht-Mitglied: Adresse ändern + anderer Haushalt.
  const [frageAddr, setFrageAddr] = useState<Record<string, boolean | null>>({});
  // Adresse (Nicht-Mitglied): Wohnsitz immer; bei „Wohnsitz ≠ Postanschrift" optional
  // eine separate Postanschrift.
  const [addrForm, setAddrForm] = useState<
    Record<
      string,
      {
        wohnsitz: { street: string; plz: string; ort: string; ortsteil: string };
        istPostgleich: boolean | null;
        postAdded: boolean;
        postanschrift: { street: string; plz: string; ort: string; ortsteil: string };
      }
    >
  >({});
  const [wohneinheit, setWohneinheit] = useState<Record<string, string>>({});
  const [frageAnderer, setFrageAnderer] = useState<Record<string, boolean | null>>({});
  const [andererHH, setAndererHH] = useState<
    Record<string, { addrKey: string; nameKey: string; label: string; strasse: string; plz: string; ort: string } | null>
  >({});
  const [firmaOpen, setFirmaOpen] = useState<Record<string, boolean>>({});
  // Kundenverbindung (M:N): mehrere bestehende + mehrere neue.
  const [kvGroupIds, setKvGroupIds] = useState<number[]>([]);
  const [kvNewNames, setKvNewNames] = useState<string[]>([]);
  const [kvNewDraft, setKvNewDraft] = useState<string>("");
  const [kvOptions, setKvOptions] = useState<Array<{ id: number; name: string; memberCount?: number }>>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveDone, setSaveDone] = useState(false);

  const loadList = useCallback(
    async (reset: boolean) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const off = reset ? 0 : offsetRef.current;
      const team = teamId || undefined;
      const term = q.trim() || undefined;
      try {
        let count = 0;
        let t = 0;
        if ((source === "name" || source === "relation") && showDismissed) {
          const kind: DismissKind = source === "relation" ? "relation" : "name";
          const res = await listDismissedCandidates(kind, ac.signal);
          t = res.total;
          count = res.rows.length;
          setDismissedRows((prev) => (reset ? res.rows : [...prev, ...res.rows]));
        } else if (source === "relation") {
          const res = await listRelationClusters(team, term, PAGE, off, ac.signal);
          t = res.total;
          count = res.rows.length;
          setClusters((prev) => (reset ? res.rows : [...prev, ...res.rows]));
        } else if (source === "name") {
          const res = await listNameCandidates(nameWithRelation, team, term, PAGE, off, ac.signal);
          t = res.total;
          count = res.rows.length;
          setNameRows((prev) => (reset ? res.rows : [...prev, ...res.rows]));
        } else {
          const res = await listHaushalte(status, team, term, PAGE, off, ac.signal);
          t = res.total;
          count = res.rows.length;
          setRows((prev) => (reset ? res.rows : [...prev, ...res.rows]));
        }
        setTotal(t);
        offsetRef.current = off + count;
        setHasMore(off + count < t);
      } catch (e: any) {
        if (e?.name !== "AbortError") setHasMore(false);
      }
    },
    [source, status, teamId, q, nameWithRelation, showDismissed],
  );

  useEffect(() => {
    listHaushalteTeams().then(setTeams).catch(() => undefined);
  }, []);

  // Auf dieser Seite (viele Reloads beim Bereinigen) den globalen „Seite
  // verlassen?"-Nag bei ungelesenen Mitteilungen unterdrücken.
  useEffect(() => {
    setReloadWarningSuppressed(true);
    return () => setReloadWarningSuppressed(false);
  }, []);

  // Bestehende Kundenverbindungen für die Mehrfach-Auswahl (M:N).
  useEffect(() => {
    fetch(apiUrl("contactgroup/kundenverbindungen"))
      .then((r) => r.json())
      .then((d) => setKvOptions(Array.isArray(d) ? d : []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (selected) return;
    const t = setTimeout(() => loadList(true), 250);
    return () => clearTimeout(t);
  }, [source, status, teamId, q, nameWithRelation, showDismissed, selected, loadList]);

  // Undo-Toast nach kurzer Zeit selbst ausblenden.
  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), 6000);
    return () => clearTimeout(t);
  }, [undo]);

  /** „keine Bereinigung nötig" — Kandidat ausblenden (optimistisch) + Undo anbieten. */
  const handleDismiss = async (kind: DismissKind, refKey: string, label: string) => {
    try {
      await dismissCandidate(kind, refKey, label);
      if (kind === "name") setNameRows((prev) => prev.filter((r) => r.nameKey !== refKey));
      else setClusters((prev) => prev.filter((r) => String(r.clusterId) !== refKey));
      setTotal((t) => Math.max(0, t - 1));
      setUndo({ kind, refKey, label });
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    }
  };

  /** Zuletzt ausgeblendeten Kandidaten wieder aufnehmen (Toast „Rückgängig"). */
  const handleUndo = async () => {
    if (!undo) return;
    const u = undo;
    setUndo(null);
    try {
      await restoreCandidate(u.kind, u.refKey);
      loadList(true);
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    }
  };

  /** „Ausgeblendete"-Ansicht: Kandidat wieder in die Worklist aufnehmen. */
  const handleRestore = async (kind: DismissKind, refKey: string) => {
    try {
      await restoreCandidate(kind, refKey);
      setDismissedRows((prev) => prev.filter((r) => r.refKey !== refKey));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    }
  };

  /**
   * Stabiler Schlüssel eines Mitglieds. Beim Anker-Einstieg aus den Kandidatenlisten
   * gibt es Mitglieder ohne Kandidaten-Zeile — dort trägt die contactId den Schlüssel.
   */
  const ckey = (m: { candidateId: number | null; contactId: number | null }) =>
    m.candidateId != null ? `c${m.candidateId}` : `x${m.contactId}`;

  /** Gesamten Detail-State verwerfen (beide Einstiege teilen sich das). */
  const resetDetailState = () => {
    setDetail(null);
    setForms({});
    setAdded([]);
    setPairLabels({});
    setPairRev({});
    setFreeMode({});
    setAnchorKey(null);
    setAddDraft(null);
    setInfoOpen({});
    setRelInfoOpen({});
    setRelOpen({});
    setPersonRels({});
    setFirmaEntries({});
    setMensch({});
    setMenschImUnternehmen({});
    setMenschImUntFirma({});
    setFrageBez({});
    setFrageHH({});
    setFrageAddr({});
    setAddrForm({});
    setWohneinheit({});
    setFrageAnderer({});
    setAndererHH({});
    setFirmaOpen({});
    setKvGroupIds([]);
    setKvNewNames([]);
    setKvNewDraft("");
    setSaveError(null);
    setSaveDone(false);
  };

  /** Geladenes Detail in den Formular-State übernehmen. */
  const applyDetail = (d: HouseholdDetail) => {
    setDetail(d);
    {
      const f: Record<string, MemberForm> = {};
      for (const m of d.members) {
        f[ckey(m)] = {
          role: roleKeyFromLabel(m.contactHouseholdRole ?? m.householdRole),
          exclude: false,
        };
      }
      setForms(f);
      setAnchorKey(d.members.length ? ckey(d.members[0]) : null);
      // M:N: bestehende KVs werden read-only angezeigt; die Zuweisungs-Chips
      // bleiben leer (man fügt nur NEUE Zuordnungen hinzu).

      // pairLabels aus bestehenden Relationen seeden (kanonisch: „was der spätere
      // für den früheren ist", Reihenfolge = Mitglieder-Reihenfolge).
      const order = d.members.map((m) => ckey(m));
      const byContact = new Map<number, string>();
      for (const m of d.members) if (m.contactId != null) byContact.set(m.contactId, ckey(m));
      const gOf = (k: string): "m" | "f" | null => {
        const m = d.members.find((x) => ckey(x) === k);
        return m ? genderKey(m.gender, m.anrede) : null;
      };
      const seeded: Record<string, string> = {};
      const seededRev: Record<string, string> = {};
      for (const r of d.relations ?? []) {
        const ks = byContact.get(r.srcId);
        const kd = byContact.get(r.destId);
        if (!ks || !kd || !r.relationType) continue;
        const iSrc = order.indexOf(ks);
        const iDst = order.indexOf(kd);
        if (iSrc < 0 || iDst < 0) continue;
        const earlier = iSrc <= iDst ? ks : kd;
        const later = iSrc <= iDst ? kd : ks;
        const rev = (r.reverseRelationType ?? "").trim();
        // gespeichert: relationType beschreibt dest (= r.destId).
        // pairLabel = „was später für früher ist".
        const pk = `${earlier}|${later}`;
        if (kd === later) {
          seeded[pk] = r.relationType; // dest = later → relationType passt direkt
          if (rev) seededRev[pk] = rev; // reverse = „was earlier(src) für later ist"
        } else {
          // dest = earlier → Richtungen tauschen. Freitext-Gegenrichtung schlägt
          // die Berechnung, sonst wie bisher über reverseRelationLabel.
          seeded[pk] = rev || reverseRelationLabel(r.relationType, gOf(later));
          if (rev) seededRev[pk] = r.relationType;
        }
      }
      setPairLabels(seeded);
      setPairRev(seededRev);

      // „Weitere Beziehungen zu Personen" (Beziehungen zu Nicht-Mitgliedern) aus den
      // bestehenden Relationen seeden — so erscheinen sie im IMMER offenen Adder als
      // editierbare Einträge und können direkt geschärft werden (Kern der Unklar-
      // Bereinigung). Das Speichern schärft die vorhandene Relation in-place.
      const seededPersonRels: Record<string, PersonRelEntry[]> = {};
      for (const m of d.members) {
        const rels = m.weitereRelations ?? [];
        if (!rels.length) continue;
        seededPersonRels[ckey(m)] = rels.map((r) => ({
          id: uid(),
          ref: { contactId: r.otherId, type: "Person", displayName: r.otherName } as ContactRef,
          label: r.relationType ?? "",
          reverseLabel: "",
          sameHousehold: r.sameHousehold,
        }));
      }
      setPersonRels(seededPersonRels);
    }
  };

  /** Detail über einen Lade-Vorgang öffnen (Anschrift ODER Anker-Kontakt). */
  const openWith = async (
    marker: { addrKey: string; nameKey: string } | { anchorId: number },
    load: () => Promise<HouseholdDetail>,
  ) => {
    setSelected(marker);
    resetDetailState();
    setDetailLoading(true);
    try {
      applyDetail(await load());
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (addrKey: string, nameKey: string) =>
    openWith({ addrKey, nameKey }, () => fetchHaushalt(addrKey, nameKey));

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    loadList(true);
  };

  const setForm = (key: string, patch: Partial<MemberForm>) =>
    setForms((prev) => ({ ...prev, [key]: { ...(prev[key] ?? { role: null, exclude: false }), ...patch } }));

  // ── Vereinheitlichte Mitgliederliste (Reihenfolge = Anker-Reihenfolge) ──────
  /**
   * Mitglieder, die bisher in einem anderen Haushalt geführt werden — beim Einstieg
   * aus den Kandidatenlisten der Normalfall. Speichern übernimmt sie hierher, deshalb
   * der Warnhinweis über der Mitgliederliste.
   */
  const foreignMembers = useMemo(
    () => (detail?.members ?? []).filter((m) => !!m.otherHouseholdKey),
    [detail],
  );

  const umembers: UMember[] = useMemo(() => {
    const ex = (detail?.members ?? []).map((m) => ({ key: ckey(m), existing: m }));
    const ad = added.map((a) => ({ key: a.key, added: a }));
    return [...ex, ...ad];
  }, [detail, added]);

  const orderIndex = useCallback((key: string) => umembers.findIndex((u) => u.key === key), [umembers]);
  const nameOfU = (u: UMember): string => (u.existing ? memberName(u.existing) : refDisplayName(u.added!.ref));
  const genderOfU = (u: UMember): "m" | "f" | null =>
    u.existing ? genderKey(u.existing.gender, u.existing.anrede) : genderKey(u.added!.ref.gender, u.added!.ref.salutationLabel);
  const rawGenderOfU = (u: UMember): string => {
    const raw = (u.existing ? u.existing.gender : u.added!.ref.gender) || "";
    if (raw.trim()) return raw.trim();
    const gk = genderOfU(u);
    return gk === "m" ? "Männlich" : gk === "f" ? "Weiblich" : "";
  };
  const roleOf = (key: string): HouseholdRoleKey | null => forms[key]?.role ?? null;
  const excludedOf = (key: string): boolean => !!forms[key]?.exclude;

  // Kanonisch [früher, später] + Default-Ableitung „was später für früher ist".
  const canonical = useCallback(
    (k1: string, k2: string): [string, string] => (orderIndex(k1) <= orderIndex(k2) ? [k1, k2] : [k2, k1]),
    [orderIndex],
  );
  const defaultPairLabel = (earlier: string, later: string): string => {
    const laterU = umembers.find((u) => u.key === later);
    return deriveRelationLabel(roleOf(earlier), roleOf(later), laterU ? genderOfU(laterU) : null);
  };
  /** „was other für viewer ist" (Anzeige in viewer-Karte / Tabellen-Zeile). */
  const labelFor = (viewerKey: string, otherKey: string): string => {
    const [earlier, later] = canonical(viewerKey, otherKey);
    const pk = `${earlier}|${later}`;
    const stored = pairLabels[pk] ?? defaultPairLabel(earlier, later);
    if (viewerKey === earlier) return stored; // other = later
    // other = earlier: explizite Freitext-Gegenrichtung schlägt die Berechnung.
    if (pairRev[pk]) return pairRev[pk];
    const otherU = umembers.find((u) => u.key === otherKey);
    return reverseRelationLabel(stored, otherU ? genderOfU(otherU) : null);
  };
  const setLabelFor = (viewerKey: string, otherKey: string, chosen: string) => {
    const [earlier, later] = canonical(viewerKey, otherKey);
    const pk = `${earlier}|${later}`;
    let stored = chosen;
    if (viewerKey !== earlier) {
      const viewerU = umembers.find((u) => u.key === viewerKey); // viewer = later
      stored = reverseRelationLabel(chosen, viewerU ? genderOfU(viewerU) : null);
    }
    setPairLabels((prev) => ({ ...prev, [pk]: stored }));
    // Katalog-Label gewählt → eine evtl. hinterlegte Freitext-Gegenrichtung gilt nicht mehr.
    setPairRev((prev) => {
      if (!prev[pk]) return prev;
      const n = { ...prev };
      delete n[pk];
      return n;
    });
  };

  /** „was viewer für other ist" — Gegenrichtung eines Freitext-Paares. */
  const revLabelFor = (viewerKey: string, otherKey: string): string => {
    const [earlier, later] = canonical(viewerKey, otherKey);
    const pk = `${earlier}|${later}`;
    return viewerKey === earlier ? pairRev[pk] ?? "" : pairLabels[pk] ?? "";
  };
  /**
   * Freitext-Paar setzen. `fwd` = „was other für viewer ist", `rev` = umgekehrt;
   * gespeichert wird immer kanonisch (früher → später).
   */
  const setFreeFor = (viewerKey: string, otherKey: string, fwd: string, rev: string) => {
    const [earlier, later] = canonical(viewerKey, otherKey);
    const pk = `${earlier}|${later}`;
    const [storeFwd, storeRev] = viewerKey === earlier ? [fwd, rev] : [rev, fwd];
    setPairLabels((prev) => ({ ...prev, [pk]: storeFwd }));
    setPairRev((prev) => ({ ...prev, [pk]: storeRev }));
  };

  /** Beim manuellen Beziehungs-Setzen die (noch leeren) Rollen beider Personen ableiten. */
  const applyRolesFromLabel = (viewerKey: string, otherKey: string, chosen: string) => {
    const { self, other } = rolesFromRelationLabel(chosen);
    setForms((prev) => {
      const next = { ...prev };
      const ensure = (k: string, role: HouseholdRoleKey | null) => {
        if (role && !next[k]?.role) next[k] = { ...(next[k] ?? { role: null, exclude: false }), role };
      };
      ensure(viewerKey, self);
      ensure(otherKey, other);
      return next;
    });
  };

  const addMember = () => {
    const ref = addDraft;
    if (!ref) return;
    const ok = ref.contactId || (ref.lastName && ref.lastName.trim());
    if (!ok) return;
    const key = `a${(_addSeq++).toString(36)}`;
    setAdded((prev) => [...prev, { key, ref }]);
    setForms((prev) => ({ ...prev, [key]: { role: null, exclude: false } }));
    setAddDraft(null);
  };

  const removeAdded = (key: string) => {
    setAdded((prev) => prev.filter((a) => a.key !== key));
    setForms((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    if (anchorKey === key) setAnchorKey(umembers[0]?.key ?? null);
  };

  const refOk = (ref: ContactRef | null): boolean => !!(ref && (ref.contactId || (ref.lastName && ref.lastName.trim())));

  // R3: „Mensch im Unternehmen = Ja" verlangt eine Firma (Pflicht) → Speichern blocken.
  const miuFirmaMissing = umembers.some(
    (u) => menschImUnternehmen[u.key] === true && !refOk(menschImUntFirma[u.key] ?? null),
  );

  const mapPersonRels = (key: string): HouseholdPersonRelInput[] =>
    (personRels[key] ?? [])
      .filter((it) => refOk(it.ref) && it.label.trim())
      .map((it) => ({
        contactId: it.ref!.contactId ?? null,
        ref: it.ref!.contactId
          ? null
          : {
              lastName: it.ref!.lastName,
              firstName: it.ref!.firstName,
              gender: it.ref!.gender,
              salutationId: it.ref!.salutationId,
              salutationLabel: it.ref!.salutationLabel,
              birthDate: it.ref!.birthDate,
            },
        label: it.label,
        reverseLabel: (it.reverseLabel ?? "").trim() || null,
        sameHousehold: it.sameHousehold,
      }));

  // VerantwortlichkeitValue je Firma-Eintrag → flache firmaRelations (eine Rolle je Eintrag, wie AP).
  const mapFirmaRels = (key: string): HouseholdFirmaRelInput[] => {
    const out: HouseholdFirmaRelInput[] = [];
    for (const e of firmaEntries[key] ?? []) {
      const f = e.firmaRef;
      if (!refOk(f) || !e.verant.roles.length) continue;
      const firma = f!.contactId
        ? { firmaContactId: f!.contactId }
        : { firmaRef: { lastName: f!.lastName, branche: f!.branche, legalFormId: f!.legalFormId, phone: f!.phone, email: f!.email, web: f!.web } };
      const ec = e.verant.externalCompany;
      const ext =
        e.verant.isExtern && ec
          ? ec.contactId
            ? { externalCompanyId: ec.contactId }
            : ec.lastName?.trim()
              ? { externalCompanyNew: { name: ec.lastName.trim(), phone: ec.phone, email: ec.email, web: ec.web, branche: ec.branche, legalFormId: ec.legalFormId } }
              : {}
          : {};
      for (const r of e.verant.roles) {
        if (r.kind === "gf") out.push({ ...firma, kind: "gf" });
        else if (r.kind === "gesellschafter") out.push({ ...firma, kind: "gesellschafter", share: r.share, istGeschaeftsfuehrer: r.istGeschaeftsfuehrer });
        // `share` gilt seit 08/2026 an jeder Rolle mit Quotentyp, nicht nur am
        // Gesellschafter (Kommanditist in Euro, Wohnungseigentümer als Bruchteil).
        else out.push({ ...firma, kind: "mensch", verantwortlichkeit: r.verantwortlichkeit, share: r.share, isExtern: e.verant.isExtern, ...ext });
      }
    }
    return out;
  };

  /** Nicht-Mitglied-Zusätze (Adresse ändern / Wohneinheit / anderer Haushalt) → Payload. */
  const buildNonMemberExtras = (key: string): Partial<HouseholdMemberInput> => {
    const out: Partial<HouseholdMemberInput> = {};
    if (frageAddr[key] === true) {
      const a = addrForm[key];
      if (a) {
        const trim = (x: { street: string; plz: string; ort: string; ortsteil: string }) => ({
          street: x.street.trim() || null,
          plz: x.plz.trim() || null,
          ort: x.ort.trim() || null,
          ortsteil: x.ortsteil.trim() || null,
        });
        const hasAny = (x: { street: string; plz: string; ort: string; ortsteil: string }) =>
          !!(x.street.trim() || x.plz.trim() || x.ort.trim() || x.ortsteil.trim());
        const wHas = hasAny(a.wohnsitz);
        const pHas = a.istPostgleich === false && a.postAdded && hasAny(a.postanschrift);
        if (wHas || pHas) {
          out.addressChange = {
            wohnsitz: wHas ? trim(a.wohnsitz) : null,
            postanschrift: pHas ? trim(a.postanschrift) : null,
          };
        }
      }
    } else if (frageAddr[key] === false) {
      const w = (wohneinheit[key] ?? "").trim();
      if (w) out.wohneinheit = w;
    }
    if (frageAnderer[key] === true && andererHH[key]) {
      const h = andererHH[key]!;
      out.otherHousehold = { addrKey: h.addrKey, name: h.label, strasse: h.strasse, plz: h.plz, ort: h.ort };
    }
    return out;
  };

  const onSave = async () => {
    if (!detail) return;
    if (miuFirmaMissing) {
      setSaveError('Bei „Mensch im Unternehmen = Ja" bitte je Kontakt eine Firma auswählen.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const members: HouseholdMemberInput[] = umembers.map((u) => {
        const fr = forms[u.key];
        const role = fr?.exclude ? null : fr?.role ?? null;
        const miuF = menschImUnternehmen[u.key] === true ? menschImUntFirma[u.key] ?? null : null;
        const conn = {
          isMenschImUnternehmen: !!mensch[u.key] || !!menschImUnternehmen[u.key],
          menschImUnternehmenFirma:
            miuF && refOk(miuF)
              ? miuF.contactId
                ? { firmaContactId: miuF.contactId }
                : { firmaRef: { lastName: miuF.lastName, branche: miuF.branche, legalFormId: miuF.legalFormId, phone: miuF.phone, email: miuF.email, web: miuF.web } }
              : null,
          personRelations: mapPersonRels(u.key),
          firmaRelations: mapFirmaRels(u.key),
          ...(fr?.exclude ? buildNonMemberExtras(u.key) : {}),
        };
        if (u.existing) {
          return {
            candidateId: u.existing.candidateId,
            contactId: u.existing.contactId,
            householdRole: role,
            isNotHousehold: !!fr?.exclude,
            ...conn,
          };
        }
        const ref = u.added!.ref;
        return {
          contactId: ref.contactId ?? null,
          ref: ref.contactId
            ? null
            : {
                lastName: ref.lastName,
                firstName: ref.firstName,
                gender: ref.gender,
                salutationId: ref.salutationId,
                salutationLabel: ref.salutationLabel,
                birthDate: ref.birthDate,
              },
          householdRole: role,
          isNotHousehold: false,
          ...conn,
        };
      });

      // Beziehungen je Paar eingeschlossener Mitglieder (aIdx = früher, bIdx = später).
      const relations: HouseholdRelationInput[] = [];
      for (let i = 0; i < umembers.length; i++) {
        if (excludedOf(umembers[i].key)) continue;
        for (let j = i + 1; j < umembers.length; j++) {
          if (excludedOf(umembers[j].key)) continue;
          const a = umembers[i].key;
          const b = umembers[j].key;
          const label = (pairLabels[`${a}|${b}`] ?? defaultPairLabel(a, b)).trim();
          const reverseLabel = (pairRev[`${a}|${b}`] ?? "").trim() || null;
          if (label) relations.push({ aIdx: i, bIdx: j, label, reverseLabel });
        }
      }

      // Kundenverbindungen (M:N): bestehende + neue (inkl. nicht bestätigtem
      // Entwurf im Eingabefeld).
      const pendingNew = [...kvNewNames, kvNewDraft.trim()].filter((n) => n.trim());
      const kundenverbindungen =
        kvGroupIds.length || pendingNew.length
          ? { groupIds: kvGroupIds, newNames: pendingNew }
          : undefined;

      await saveHaushalt(detail.addrKey, members, relations, kundenverbindungen);
      setSaveDone(true);
      setTimeout(() => closeDetail(), 1100);
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const renderStatusChip = (label?: string | null, extra?: string[]) => {
    const chips: React.ReactNode[] = [];
    if (label) {
      const c = STATUS_COLORS[label] ?? { bg: "#eee", fg: "#444" };
      chips.push(
        <span key="primary" className={s.statusChip} style={{ backgroundColor: c.bg, color: c.fg }}>
          {label}
        </span>,
      );
    }
    for (const e of extra ?? []) {
      chips.push(
        <span key={e} className={s.statusChip} style={{ backgroundColor: "#eef0f6", color: "#4a4a6a" }}>
          {e}
        </span>,
      );
    }
    return chips;
  };

  const renderGenderPill = (u: UMember) => {
    const g = rawGenderOfU(u);
    if (!g) return null;
    const vis = GENDER_VIS[g] ?? { sym: "•", color: BRAND.muted };
    return (
      <span className={s.genderPill} style={{ color: vis.color }} title={`Geschlecht: ${g}`}>
        <span style={{ fontSize: "0.95rem" }}>{vis.sym}</span>
        {g}
      </span>
    );
  };

  /**
   * Ein Label, das nicht (mehr) im Katalog steht — z. B. Freitext oder Altbestand
   * („Verwandt"). Es wird als „Sonstiges (Freitext)" angezeigt und ist damit direkt
   * überschreibbar, statt als stiller Extra-Eintrag im Dropdown zu überleben.
   */
  const isFreeLabel = (v: string): boolean => !!v.trim() && !relationLabels.includes(v);

  /**
   * Beziehungs-Dropdown inkl. Freitext-Option. `fwd` = „was other für viewer ist",
   * `rev` = die Gegenrichtung. Bei Freitext werden BEIDE Richtungen erfasst — sie
   * lassen sich nicht auseinander berechnen (anders als Katalog-Labels).
   */
  const renderRelPicker = (opts: {
    /** Stabiler Schlüssel der Zeile — merkt sich den Freitext-Modus bei leerem Text. */
    rowKey: string;
    fwd: string;
    rev: string;
    viewerName: string;
    otherName: string;
    /** Nachlauf hinter der Auswahl („von <Name>") — steht vor dem Freitext-Block. */
    trailing?: React.ReactNode;
    onPick: (label: string) => void;
    onFree: (fwd: string, rev: string) => void;
  }) => {
    const free = freeMode[opts.rowKey] || isFreeLabel(opts.fwd);
    return (
      <>
        <select
          className={s.selectSm}
          value={free ? RELATIONSHIP_FREE : opts.fwd}
          onChange={(e) => {
            const v = e.target.value;
            if (v === RELATIONSHIP_FREE) {
              setFreeMode((p) => ({ ...p, [opts.rowKey]: true }));
              // Katalog-Wert räumen — sonst bliebe er als „Freitext" stehen.
              opts.onFree(isFreeLabel(opts.fwd) ? opts.fwd : "", opts.rev);
            } else {
              setFreeMode((p) => ({ ...p, [opts.rowKey]: false }));
              opts.onPick(v);
            }
          }}
        >
          <option value="">— wählen —</option>
          {relationLabels.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
          <option value={RELATIONSHIP_FREE}>Sonstiges (Freitext) …</option>
        </select>
        {opts.trailing}
        {free && (
          <div className={s.freeRelBox}>
            <label className={s.freeRelRow}>
              <span>{opts.otherName} ist</span>
              <input
                className={s.freeInput}
                placeholder="z. B. Patenkind"
                value={opts.fwd}
                onChange={(e) => opts.onFree(e.target.value, opts.rev)}
              />
              <span>von {opts.viewerName}</span>
            </label>
            <label className={s.freeRelRow}>
              <span>{opts.viewerName} ist</span>
              <input
                className={s.freeInput}
                placeholder="Gegenrichtung, z. B. Patentante"
                value={opts.rev}
                onChange={(e) => opts.onFree(opts.fwd, e.target.value)}
              />
              <span>von {opts.otherName}</span>
            </label>
          </div>
        )}
      </>
    );
  };

  const yesNo = (value: boolean | null, onPick: (v: boolean) => void) => (
    <span className={s.yesNoWrap}>
      <button className={mergeClasses(s.yesNoBtn, value === true && s.yesNoActive)} onClick={() => onPick(true)}>Ja</button>
      <button className={mergeClasses(s.yesNoBtn, value === false && s.yesNoActive)} onClick={() => onPick(false)}>Nein</button>
    </span>
  );

  /** Info-Icon mit Tooltip (Erklärung zur Frage). */
  const infoTip = (text: string) => (
    <Tooltip content={text} relationship="label" positioning="after">
      <span
        tabIndex={0}
        style={{ display: "inline-flex", alignItems: "center", color: BRAND.purple, cursor: "help", marginLeft: 6, verticalAlign: "middle" }}
      >
        <Info16Regular />
      </span>
    </Tooltip>
  );

  /** Adder für „weitere Beziehungen zu Personen" (beidseitig). sameHousehold = eigener Haushalt. */
  /** Beziehungen eines Nicht-Mitglieds zu den HAUSHALTSPERSONEN (Auswahl, keine Suche). */
  const renderHouseholdMemberRels = (contactKey: string) => {
    const contactU = umembers.find((u) => u.key === contactKey);
    const contactName = contactU ? nameOfU(contactU) : "Kontakt";
    const cidOf = (u: UMember): number | null => u.existing?.contactId ?? u.added?.ref.contactId ?? null;
    const others = umembers.filter((u) => u.key !== contactKey && !excludedOf(u.key) && cidOf(u));
    const entries = personRels[contactKey] ?? [];
    const setRel = (otherU: UMember, label: string, reverseLabel = "") => {
      const oid = cidOf(otherU);
      if (!oid) return;
      setPersonRels((p) => {
        const rest = (p[contactKey] ?? []).filter((e) => !(e.sameHousehold === false && e.ref?.contactId === oid));
        if (!label && !reverseLabel) return { ...p, [contactKey]: rest };
        const ref: ContactRef = {
          contactId: oid,
          type: "Person",
          displayName: nameOfU(otherU),
          gender: otherU.existing?.gender ?? otherU.added?.ref.gender,
          salutationLabel: otherU.existing?.anrede ?? otherU.added?.ref.salutationLabel,
        };
        return { ...p, [contactKey]: [...rest, { id: uid(), ref, label, reverseLabel, sameHousehold: false }] };
      });
    };
    if (!others.length) return <div className={s.adderBox}><span className={s.tableHint}>Keine weiteren Haushaltspersonen.</span></div>;
    return (
      <div className={s.adderBox}>
        {others.map((o) => {
          const oid = cidOf(o);
          const ent = entries.find((e) => e.sameHousehold === false && e.ref?.contactId === oid);
          return (
            <div key={o.key} className={s.relCardRow}>
              <span className={s.roleLabel}>Beziehung:</span>
              <span>{nameOfU(o)} ist</span>
              {renderRelPicker({
                rowKey: `hm-${contactKey}-${o.key}`,
                fwd: ent?.label ?? "",
                rev: ent?.reverseLabel ?? "",
                viewerName: contactName,
                otherName: nameOfU(o),
                trailing: <span>von {contactName}</span>,
                onPick: (label) => setRel(o, label),
                onFree: (fwd, rev) => setRel(o, fwd, rev),
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPersonRelAdder = (key: string, sameHousehold: boolean) => {
    const all = personRels[key] ?? [];
    const entries = all.filter((e) => e.sameHousehold === sameHousehold);
    const setEntries = (next: PersonRelEntry[]) =>
      setPersonRels((p) => ({ ...p, [key]: [...(p[key] ?? []).filter((e) => e.sameHousehold !== sameHousehold), ...next] }));
    const add = () => setEntries([...entries, { id: uid(), ref: null, label: "", sameHousehold }]);
    const update = (id: string, patch: Partial<PersonRelEntry>) => setEntries(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    const remove = (id: string) => setEntries(entries.filter((e) => e.id !== id));
    const memberU = umembers.find((u) => u.key === key);
    const gMember = memberU ? genderOfU(memberU) : null;
    const memberLabel = memberU ? nameOfU(memberU) : "";
    // Bereits verknüpfte Personen aus der Suche ausblenden: bestehende
    // (DB-)Beziehungen + alle laufend erfassten Einträge dieses Mitglieds.
    const excludeIds = [
      ...(memberU?.existing?.weitereRelations ?? []).map((r) => r.otherId),
      ...all.map((e) => e.ref?.contactId),
    ].filter((x): x is number => x != null);
    return (
      <div className={s.adderBox}>
        {entries.map((e) => {
          const otherName = e.ref ? refDisplayName(e.ref) : "die Person";
          // Freitext-Gegenrichtung schlägt die Berechnung (die kann Freitext nicht umkehren).
          const reverse = (e.reverseLabel ?? "").trim() || (e.label ? reverseRelationLabel(e.label, gMember) : "");
          const selCid = e.ref?.contactId ?? null;
          const infoOpen = !!relInfoOpen[e.id];
          // Vererbung an Mit-Haushaltsmitglieder: für jedes andere Mitglied N mit
          // bekannter Rolle die aus der Haushaltsstruktur abgeleitete Beziehung von
          // C zu N berechnen (nur familiäre Labels, eindeutige Fälle). Reine
          // Vorschau — die Persistenz übernimmt das Backend beim Speichern.
          const roleMember = roleOf(key);
          const genderC = e.ref ? genderKey(e.ref.gender, e.ref.salutationLabel) : null;
          const inherits =
            e.label && e.ref
              ? umembers
                  .filter((o) => o.key !== key && !excludedOf(o.key))
                  .map((o) => ({ o, derived: composeKinship(e.label, linkKind(roleMember, roleOf(o.key)), genderC) }))
                  .filter((x): x is { o: typeof x.o; derived: string } => x.derived != null)
              : [];
          return (
            <div key={e.id} className={s.adderEntry}>
              <ContactRefField wantType="Person" personOnly idBase={`pr-${key}-${e.id}`} value={e.ref} onChange={(ref) => update(e.id, { ref })} excludeIds={excludeIds} />
              {selCid != null && (
                <>
                  <button className={s.expanderBtn} onClick={() => setRelInfoOpen((p) => ({ ...p, [e.id]: !infoOpen }))}>
                    {infoOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    Bisherige Informationen {infoOpen ? "ausblenden" : "anzeigen"}
                  </button>
                  {infoOpen && (
                    <div className={s.editPanelWrap}>
                      <CleanupContactEditPanel key={selCid} contactId={selCid} />
                    </div>
                  )}
                </>
              )}
              <div className={s.roleRow}>
                <span className={s.roleLabel}>Beziehung:</span>
                <span><strong>{otherName}</strong> ist</span>
                {renderRelPicker({
                  rowKey: `pr-${e.id}`,
                  fwd: e.label,
                  rev: e.reverseLabel ?? "",
                  viewerName: memberLabel,
                  otherName,
                  trailing: <span>von {memberLabel}</span>,
                  onPick: (label) => update(e.id, { label, reverseLabel: "" }),
                  onFree: (fwd, rev) => update(e.id, { label: fwd, reverseLabel: rev }),
                })}
                <button className={s.expanderBtn} style={{ color: "#b3261e" }} onClick={() => remove(e.id)}>entfernen</button>
              </div>
              {e.label && e.ref && reverse && (
                <div className={s.connExisting}>
                  <div>• Umgekehrt: {memberLabel} ist <strong>{reverse}</strong> von {otherName}</div>
                </div>
              )}
              {inherits.length > 0 && (
                <div className={s.connExisting}>
                  {inherits.map(({ o, derived }) => (
                    <div key={o.key}>→ wird auch bei <strong>{nameOfU(o)}</strong> als <strong>{derived}</strong> gesetzt</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <button className={s.ghostBtn} onClick={add}>+ Person / Beziehung</button>
      </div>
    );
  };

  /** Adder für „Verbindungen zu Firmen" (Firma + Verantwortlichkeit wie AP). */
  const renderFirmaAdder = (key: string) => {
    const entries = firmaEntries[key] ?? [];
    const setEntries = (next: FirmaEntry[]) => setFirmaEntries((p) => ({ ...p, [key]: next }));
    const add = () => setEntries([...entries, { id: uid(), firmaRef: null, verant: blankVerant() }]);
    const update = (id: string, patch: Partial<FirmaEntry>) => setEntries(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    const remove = (id: string) => setEntries(entries.filter((e) => e.id !== id));
    const memberU = umembers.find((u) => u.key === key);
    // Bereits verbundene Firmen aus der Suche ausblenden: bestehende
    // (DB-)Verbindungen + alle laufend erfassten Firmen-Einträge dieses Mitglieds.
    const excludeIds = [
      ...(memberU?.existing?.firmaRelations ?? []).map((r) => r.otherId),
      ...entries.map((e) => e.firmaRef?.contactId),
    ].filter((x): x is number => x != null);
    return (
      <div className={s.adderBox}>
        {entries.map((e) => (
          <div key={e.id} className={s.adderEntry}>
            <span className={s.roleLabel}>Firma</span>
            <ContactRefField wantType="Firma" firmaExtraFields idBase={`fr-${key}-${e.id}`} value={e.firmaRef} onChange={(ref) => update(e.id, { firmaRef: ref })} excludeIds={excludeIds} />
            {/* Die Rechtsform der oben gewählten Firma entscheidet, welche Organrollen es gibt. */}
            <VerantwortlichkeitInput idBase={`fv-${key}-${e.id}`} value={e.verant} onChange={(v) => update(e.id, { verant: v })} legalFormId={e.firmaRef?.legalFormId} />
            <button className={s.expanderBtn} style={{ color: "#b3261e", alignSelf: "flex-start" }} onClick={() => remove(e.id)}>entfernen</button>
          </div>
        ))}
        <button className={s.ghostBtn} onClick={add}>+ Verbindung zur Firma</button>
      </div>
    );
  };

  /** Adress-Felder für „Adresse ändern" (Nicht-Mitglied): Wohnsitz + optionale Postanschrift. */
  const renderAddressChange = (key: string) => {
    const blank = { street: "", plz: "", ort: "", ortsteil: "" };
    const a = addrForm[key] ?? { wohnsitz: { ...blank }, istPostgleich: null as boolean | null, postAdded: false, postanschrift: { ...blank } };
    const set = (patch: Partial<typeof a>) => setAddrForm((p) => ({ ...p, [key]: { ...a, ...patch } }));
    const fields = (
      addr: { street: string; plz: string; ort: string; ortsteil: string },
      upd: (patch: Partial<{ street: string; plz: string; ort: string; ortsteil: string }>) => void,
      idBase: string,
    ) => (
      <>
        <AddressAutocomplete
          label="Straße & Nr."
          name={`${idBase}-street`}
          value={addr.street}
          onChange={(v) => upd({ street: v })}
          onResolved={(r) => upd({ street: r.street, plz: r.postalcode, ort: r.city, ortsteil: r.cityArea })}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <input className={s.freeInput} style={{ maxWidth: 130 }} placeholder="PLZ" value={addr.plz} onChange={(e) => upd({ plz: e.target.value })} />
          <input className={s.freeInput} placeholder="Ort" value={addr.ort} onChange={(e) => upd({ ort: e.target.value })} />
        </div>
        <input className={s.freeInput} placeholder="Ortsteil (optional)" value={addr.ortsteil} onChange={(e) => upd({ ortsteil: e.target.value })} />
      </>
    );
    return (
      <div className={s.adderBox}>
        {fields(a.wohnsitz, (patch) => set({ wohnsitz: { ...a.wohnsitz, ...patch } }), `addr-w-${key}`)}
        <div className={s.qRow}>
          <span className={s.roleLabel}>Ist der Wohnsitz zugleich die Postanschrift?</span>
          {yesNo(a.istPostgleich, (v) => set(v ? { istPostgleich: true, postAdded: false } : { istPostgleich: false }))}
        </div>
        {a.istPostgleich === false && !a.postAdded && (
          <button className={s.ghostBtn} onClick={() => set({ postAdded: true })}>+ Postanschrift hinzufügen</button>
        )}
        {a.istPostgleich === false && a.postAdded && (
          <div className={s.adderBox}>
            <span className={s.tableHint}>Abweichende Postanschrift</span>
            {fields(a.postanschrift, (patch) => set({ postanschrift: { ...a.postanschrift, ...patch } }), `addr-p-${key}`)}
          </div>
        )}
      </div>
    );
  };

  const renderMemberCard = (u: UMember, idx: number) => {
    const fr = forms[u.key] ?? { role: null, exclude: false };
    const open = !!infoOpen[u.key];
    const relShown = !!relOpen[u.key];
    const contactId = u.existing?.contactId ?? u.added?.ref.contactId ?? null;
    const others = umembers.filter((o) => o.key !== u.key && !excludedOf(o.key));

    // Stammdaten aus BEIDEN Quellen (Contact + Kandidaten-Import) mergen, damit nie etwas leer ist.
    const m = u.existing;
    const geb = m ? m.birthDate ?? m.geburtsdatum : u.added!.ref.birthDate;
    const alter = ageFromBirthDate(geb);
    const knr = m?.kundennummer ?? null;
    const previewInfo = m
      ? {
          ...m,
          birthDate: m.birthDate ?? m.geburtsdatum,
          salutationLabel: m.salutationLabel ?? m.anrede,
          address:
            m.address ??
            (m.strasse || m.plz || m.ort
              ? { street: m.strasse ?? undefined, postalCode: m.plz ?? undefined, city: m.ort ?? undefined }
              : null),
        }
      : { salutationLabel: u.added!.ref.salutationLabel, birthDate: u.added!.ref.birthDate, gender: u.added!.ref.gender };

    return (
      <div key={u.key} className={mergeClasses(s.memberCard, fr.exclude && s.memberCardExcluded)}>
        <div className={s.memberTop}>
          <span className={s.memberNo}>{idx + 1}.</span>
          <span className={s.memberName}>{nameOfU(u)}</span>
          {renderGenderPill(u)}
          {u.existing && renderStatusChip(u.existing.statusLabel, u.existing.extraStatuses)}
          {u.added && <span className={s.countPill}>hinzugefügt</span>}
          {u.added && (
            <button className={s.expanderBtn} style={{ color: "#b3261e" }} onClick={() => removeAdded(u.key)}>
              entfernen
            </button>
          )}
        </div>

        {(geb || knr) && (
          <div className={s.metaLine}>
            {geb && <span>Geb.: {fmtDate(geb)}{alter != null ? ` (${alter} J.)` : ""}</span>}
            {knr && <span>Kd.-Nr.: {knr}</span>}
          </div>
        )}

        <CleanupContactPreview info={previewInfo} />

        {contactId != null && (
          <>
            <button className={s.expanderBtn} onClick={() => setInfoOpen((p) => ({ ...p, [u.key]: !open }))}>
              {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
              Bisherige Informationen {open ? "ausblenden" : "anzeigen"}
            </button>
            {open && (
              <div className={s.editPanelWrap}>
                <CleanupContactEditPanel contactId={contactId} />
              </div>
            )}
          </>
        )}

        <div className={s.roleRow}>
          <span className={s.roleLabel}>Welche Rolle hat der Kontakt im Haushalt?</span>
          <select
            className={s.select}
            value={fr.role ?? ""}
            disabled={fr.exclude}
            onChange={(e) => setForm(u.key, { role: (e.target.value || null) as HouseholdRoleKey | null })}
          >
            <option value="">— wählen —</option>
            {householdRoles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          {fr.role === "sonstige" && !fr.exclude && (
            <span className={s.tableHint}>
              Aus „Sonstige" wird keine Beziehung abgeleitet — sie unten je Person als
              „Sonstiges (Freitext)" erfassen.
            </span>
          )}
          {u.existing && (
            <label className={s.excludeRow}>
              <input type="checkbox" checked={fr.exclude} onChange={(e) => setForm(u.key, { exclude: e.target.checked })} />
              kein Haushaltsmitglied
            </label>
          )}
        </div>

        {fr.exclude && (
          <div className={s.relCardBlock}>
            {/* (1) Adresse ändern */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>Soll die Adresse für diesen Kontakt geändert werden?</span>
              {yesNo(frageAddr[u.key] ?? null, (v) => setFrageAddr((p) => ({ ...p, [u.key]: v })))}
            </div>
            {frageAddr[u.key] === true && renderAddressChange(u.key)}
            {frageAddr[u.key] === false && (
              <div className={s.adderBox}>
                <span className={s.tableHint}>
                  Gleiche Anschrift, aber eigener Haushalt? Gib die Wohneinheit an (z. B. „Keller", „2. OG") —
                  wird als Notiz zur Adresse gespeichert.
                </span>
                <input
                  className={s.freeInput}
                  placeholder="Wohneinheit (Notiz)"
                  value={wohneinheit[u.key] ?? ""}
                  onChange={(e) => setWohneinheit((p) => ({ ...p, [u.key]: e.target.value }))}
                />
              </div>
            )}

            {/* (2) Beziehungen zu Personen im Haushalt */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>Hat der Kontakt dennoch Beziehungen zu den Personen in diesem Haushalt?</span>
              {yesNo(frageBez[u.key] ?? null, (v) => setFrageBez((p) => ({ ...p, [u.key]: v })))}
            </div>
            {frageBez[u.key] === true && renderHouseholdMemberRels(u.key)}

            {/* (3) Anderer Haushalt? */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>Gehört dieser Kontakt zu einem anderen Haushalt?</span>
              {yesNo(frageAnderer[u.key] ?? null, (v) => setFrageAnderer((p) => ({ ...p, [u.key]: v })))}
            </div>
            {frageAnderer[u.key] === true && (
              <div className={s.adderBox}>
                <span className={s.tableHint}>Wähle den Haushalt, zu dem dieser Kontakt gehört — er wird dort als Mitglied eingetragen.</span>
                <HaushaltPicker
                  value={andererHH[u.key] ?? null}
                  onChange={(v) => setAndererHH((p) => ({ ...p, [u.key]: v }))}
                  excludeAddrKey={detail?.addrKey}
                />
              </div>
            )}
            {/* (3b) Haushalt erstellen? — eigenständiger Schritt, immer sichtbar */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>Möchtest du für diesen Kontakt einen Haushalt erstellen?</span>
              {yesNo(frageHH[u.key] ?? null, (v) => setFrageHH((p) => ({ ...p, [u.key]: v })))}
            </div>
            {frageHH[u.key] === true && renderPersonRelAdder(u.key, true)}

            {/* (4) Mensch mit Verantwortlichkeit fürs Unternehmen */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>
                Ist dieser Kontakt ein Mensch mit Verantwortlichkeit fürs Unternehmen?
                {infoTip(
                  "Person mit einer konkreten Verantwortlichkeit/Funktion für die Firma (z. B. Geschäftsführer, Gesellschafter; intern oder extern). Legt eine Firmen-Verbindung mit Verantwortlichkeit an.",
                )}
              </span>
              {yesNo(mensch[u.key] ?? null, (v) => setMensch((p) => ({ ...p, [u.key]: v })))}
            </div>
            {mensch[u.key] === true && renderFirmaAdder(u.key)}

            {/* (5) Mensch im Unternehmen (Status + Pflicht-Firma) */}
            <div className={s.qRow}>
              <span className={s.roleLabel}>
                Ist der Kontakt ein Mensch im Unternehmen?
                {infoTip(
                  "Mitarbeiter oder mitversichertes Risiko, das dem Unternehmen zugeordnet, aber nicht als eigener Kontakt geführt wird. Es werden der Status sowie eine Verbindung zur gewählten Firma gesetzt (die Verbindung erscheint vorerst nicht in den Firmen-Verknüpfungen).",
                )}
              </span>
              {yesNo(menschImUnternehmen[u.key] ?? null, (v) => setMenschImUnternehmen((p) => ({ ...p, [u.key]: v })))}
            </div>
            {menschImUnternehmen[u.key] === true && (
              <div className={s.adderBox}>
                <span className={s.roleLabel}>Für welche Firma ist das ein Mensch im Unternehmen?</span>
                <ContactRefField
                  wantType="Firma"
                  firmaExtraFields
                  idBase={`miu-${u.key}`}
                  value={menschImUntFirma[u.key] ?? null}
                  onChange={(ref) => setMenschImUntFirma((p) => ({ ...p, [u.key]: ref }))}
                />
                {!refOk(menschImUntFirma[u.key] ?? null) && (
                  <span className={s.tableHint} style={{ color: "#b3261e" }}>Bitte eine Firma auswählen (Pflicht).</span>
                )}
              </div>
            )}
          </div>
        )}

        {!fr.exclude && others.length > 0 && (
          <>
            <button className={s.expanderBtn} onClick={() => setRelOpen((p) => ({ ...p, [u.key]: !relShown }))}>
              {relShown ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
              Beziehungen zu den anderen Personen {relShown ? "ausblenden" : "anzeigen"}
            </button>
            {relShown && (
              <div className={s.relCardBlock}>
                <span className={s.tableHint}>
                  Es müssen nur die Rollen gesetzt werden — die Beziehungen werden automatisch abgeleitet. Hier lassen
                  sie sich bei Bedarf manuell anpassen.
                </span>
                {others.map((o) => {
                  const cur = labelFor(u.key, o.key);
                  return (
                    <div key={o.key} className={s.relCardRow}>
                      <span className={s.roleLabel}>Beziehung:</span>
                      <span className={s.relCardName}>{nameOfU(o)} ist</span>
                      {renderRelPicker({
                        rowKey: `pair-${u.key}-${o.key}`,
                        fwd: cur,
                        rev: revLabelFor(u.key, o.key),
                        viewerName: nameOfU(u),
                        otherName: nameOfU(o),
                        trailing: <span>von {nameOfU(u)}</span>,
                        onPick: (label) => {
                          setLabelFor(u.key, o.key, label);
                          applyRolesFromLabel(u.key, o.key, label);
                        },
                        onFree: (fwd, rev) => setFreeFor(u.key, o.key, fwd, rev),
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  /** Interaktive Übersichts-Tabelle (Name | Rolle | Beziehung, Anker per Klick). */
  const renderOverviewTable = () => {
    const active = umembers.filter((u) => !excludedOf(u.key));
    if (active.length < 2) return null;
    const anchor = active.find((u) => u.key === anchorKey) ?? active[0];
    const ordered = [anchor, ...active.filter((u) => u.key !== anchor.key)];
    return (
      <div className={s.tableBox}>
        <span className={s.tableTitle}>Übersicht der Beziehungen im Haushalt</span>
        <span className={s.tableHint}>
          Beziehungen relativ zum ersten Kontakt. Auf eine Zeile klicken, um die Beziehungen zu dieser Person zu sehen.
        </span>
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.th}>Name</th>
              <th className={s.th}>Rolle</th>
              <th className={s.th}>Beziehung</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((u) => {
              const isAnchor = u.key === anchor.key;
              return (
                <tr
                  key={u.key}
                  className={mergeClasses(s.rowClickable, isAnchor && s.rowAnchor)}
                  onClick={() => setAnchorKey(u.key)}
                >
                  <td className={s.td}>
                    {nameOfU(u)}
                    {isAnchor && <span className={s.anchorTag}>(Anker)</span>}
                  </td>
                  <td className={s.td}>{roleLabelOf(roleOf(u.key))}</td>
                  <td className={s.td}>{isAnchor ? "—" : labelFor(anchor.key, u.key)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  /** „Weitere Beziehungen zu Personen" — Haushalts-Mitglieder (Nicht-Mitglieder erfassen auf der Karte). */
  const renderPersonConnections = () => {
    const members = umembers.filter((u) => !excludedOf(u.key));
    if (!members.length) return null;
    return (
      <div className={s.tableBox}>
        <span className={s.tableTitle}>Weitere Beziehungen zu Personen</span>
        <span className={s.tableHint}>
          Beziehungen <strong>außerhalb des Haushalts</strong> — z. B. das Kind ist zum Studieren ausgezogen
          und gehört nicht mehr zum Haushalt, ist aber weiterhin verwandt. Je Person eine weitere Person suchen,
          Beziehung wählen — beide Richtungen werden angezeigt.
        </span>
        {members.map((u, i) => {
          const key = u.key;
          // Eingehende ABGELEITETE Beziehungen: Gegenstück zur Vorschau im
          // Ursprungs-Kasten. Aus den familiären „weiteren Beziehungen" der ANDEREN
          // Mitglieder die für DIESES Mitglied (key) abgeleitete Beziehung berechnen
          // (Bruder von A → Schwager von B …). Nur Anzeige; Persistenz macht das
          // Backend beim Speichern.
          const roleN = roleOf(key);
          const inherited = umembers
            .filter((a) => a.key !== key && !excludedOf(a.key))
            .flatMap((a) => {
              const roleA = roleOf(a.key);
              return (personRels[a.key] ?? [])
                .filter((e) => !e.sameHousehold && !!e.label && !!e.ref)
                .map((e) => {
                  const ref = e.ref!;
                  const derived = composeKinship(e.label, linkKind(roleA, roleN), genderKey(ref.gender, ref.salutationLabel));
                  return derived ? { from: nameOfU(a), cName: refDisplayName(ref), derived } : null;
                })
                .filter((x): x is { from: string; cName: string; derived: string } => x != null);
            });
          return (
            <div key={key} className={s.connMember}>
              <span className={s.connMemberName}>{i + 1}. {nameOfU(u)}</span>
              {inherited.length > 0 && (
                <div className={s.connExisting}>
                  {inherited.map((it, idx) => (
                    <div key={`inh-${idx}`}>• {it.cName}: <strong>{it.derived}</strong> <span style={{ color: BRAND.muted }}>(abgeleitet aus {it.from})</span></div>
                  ))}
                </div>
              )}
              {/* Immer offen: bestehende (geseedete) + neue Beziehungen direkt editierbar. */}
              {renderPersonRelAdder(key, false)}
            </div>
          );
        })}
      </div>
    );
  };

  /** „Verbindungen zu Firmen" — je Person bestehende + Verantwortlichkeit-Adder. */
  const renderFirmaConnections = () => {
    if (!umembers.length) return null;
    return (
      <div className={s.tableBox}>
        <span className={s.tableTitle}>Weitere Verbindungen zur Firma</span>
        <span className={s.tableHint}>Je Person eine Firma + Verantwortlichkeit (Geschäftsführer/Gesellschafter/intern-extern/Anteile/Sonstiges) hinzufügen.</span>
        {umembers.map((u, i) => {
          const key = u.key;
          const ex = u.existing?.firmaRelations ?? [];
          const open = !!firmaOpen[key];
          const cnt = firmaEntries[key]?.length ?? 0;
          return (
            <div key={key} className={s.connMember}>
              <span className={s.connMemberName}>{i + 1}. {nameOfU(u)}</span>
              {ex.length > 0 && (
                <div className={s.connExisting}>
                  {ex.map((r) => (
                    <div key={r.relationId}>
                      • {r.otherName}: <strong>{r.relationType}</strong>
                      {r.function ? ` — ${r.function}` : ""}
                      {r.share ? ` (${r.share})` : ""}
                      {r.isExtern ? " · extern" : ""}
                    </div>
                  ))}
                </div>
              )}
              <button className={s.expanderBtn} onClick={() => setFirmaOpen((p) => ({ ...p, [key]: !open }))}>
                {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                Verbindung zur Firma hinzufügen{cnt > 0 ? ` (${cnt})` : ""}
              </button>
              {open && renderFirmaAdder(key)}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * „Kundenverbindungen" (M:N) — Haushalt + direkt verwandte Personen können
   * MEHREREN Kundenverbindungen zugeordnet werden. Bestehende werden read-only
   * angezeigt; neue Zuordnungen (bestehende KV wählen ODER neue anlegen) als
   * abwählbare Chips.
   */
  const renderKundenverbindung = () => {
    // Bereits zugeordnete KVs aller Mitglieder (dedupliziert).
    const existingKvs = new Map<number, string>();
    for (const u of umembers) {
      const list =
        u.existing?.kundenverbindungen ??
        (u.existing?.kundenverbindung ? [u.existing.kundenverbindung] : []);
      for (const kv of list) existingKvs.set(kv.id, kv.name);
    }
    const nameOfKv = (id: number) =>
      kvOptions.find((o) => o.id === id)?.name ?? existingKvs.get(id) ?? `KV ${id}`;
    const availableOptions = kvOptions.filter((o) => !kvGroupIds.includes(o.id));
    const addNewKv = () => {
      const n = kvNewDraft.trim();
      if (n && !kvNewNames.some((x) => x.toLowerCase() === n.toLowerCase())) {
        setKvNewNames((p) => [...p, n]);
      }
      setKvNewDraft("");
    };
    const chipStyle: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 8px",
      borderRadius: 999,
      backgroundColor: "#f0e6f2",
      color: BRAND.purple,
      fontSize: "0.8rem",
      fontWeight: 600,
    };
    const xBtn: React.CSSProperties = {
      border: "none",
      background: "transparent",
      color: BRAND.purple,
      cursor: "pointer",
      fontSize: "0.9rem",
      lineHeight: 1,
      padding: 0,
    };
    return (
      <div className={s.tableBox}>
        <span className={s.tableTitle}>Kundenverbindungen</span>
        <span className={s.tableHint}>
          Haushalt und direkt verwandte Personen (Ehe-/Lebenspartner, Eltern, Kinder,
          Geschwister …) einer oder mehreren Kundenverbindungen zuordnen.
        </span>
        {existingKvs.size > 0 && (
          <div className={s.connExisting}>
            <div>Bereits zugeordnet:</div>
            {Array.from(existingKvs.entries()).map(([id, name]) => (
              <div key={id}>
                • <strong>{name}</strong>
              </div>
            ))}
          </div>
        )}
        {(kvGroupIds.length > 0 || kvNewNames.length > 0) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {kvGroupIds.map((id) => (
              <span key={`g${id}`} style={chipStyle}>
                {nameOfKv(id)}
                <button
                  type="button"
                  style={xBtn}
                  aria-label="Entfernen"
                  onClick={() => setKvGroupIds((p) => p.filter((x) => x !== id))}
                >
                  ×
                </button>
              </span>
            ))}
            {kvNewNames.map((n, i) => (
              <span key={`n${i}`} style={chipStyle}>
                {n} (neu)
                <button
                  type="button"
                  style={xBtn}
                  aria-label="Entfernen"
                  onClick={() => setKvNewNames((p) => p.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <LabeledSelect
          label="Bestehende Kundenverbindung hinzufügen"
          name="kv-add"
          value=""
          onChange={(v) => {
            const id = Number(v);
            if (id && !kvGroupIds.includes(id)) setKvGroupIds((p) => [...p, id]);
          }}
          options={availableOptions.map((o) => ({
            value: String(o.id),
            label: o.memberCount != null ? `${o.name} (${o.memberCount})` : o.name,
          }))}
          placeholder="auswählen…"
        />
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <input
            className={s.freeInput}
            placeholder="neue Kundenverbindung anlegen (Name)"
            value={kvNewDraft}
            onChange={(e) => setKvNewDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addNewKv();
              }
            }}
          />
          <button
            type="button"
            className={mergeClasses(s.addBtn, !kvNewDraft.trim() && s.addBtnDisabled)}
            onClick={addNewKv}
            disabled={!kvNewDraft.trim()}
          >
            Hinzufügen
          </button>
        </div>
      </div>
    );
  };

  // ─── Cluster-Detail (Tab „Unklare Beziehung") ─────────────────────────────
  if (clusterSel) {
    return (
      <div className={s.root}>
        <RelationClusterDetail
          contactIds={clusterSel}
          dismissRef={clusterDismiss}
          onDismissed={() => {
            // Gleiche Rückmeldung wie beim Ausblenden aus der Liste: „Rückgängig"-Toast.
            if (clusterDismiss) setUndo(clusterDismiss);
          }}
          onBack={() => {
            setClusterSel(null);
            setClusterDismiss(null);
            loadList(true);
          }}
        />
      </div>
    );
  }

  // ─── Detail-Ansicht ────────────────────────────────────────────────────────
  if (selected) {
    const addValid = !!(addDraft && (addDraft.contactId || (addDraft.lastName && addDraft.lastName.trim())));
    return (
      <div className={s.root}>
        <button className={s.backBtn} onClick={closeDetail}>
          <ArrowLeft20Regular /> Zurück zur Haushalts-Liste
        </button>

        {detailLoading && <div className={s.empty}>Lade Haushalt…</div>}

        {detail && (
          <>
            <div className={s.detailHead}>
              <span className={s.detailAddr}>{addrLine(detail.address) || "Haushalt"}</span>
              <span className={s.detailSub}>{umembers.length} Mitglieder</span>
            </div>

            {foreignMembers.length > 0 && (
              <div className={s.warnBox}>
                <strong>Achtung:</strong>{" "}
                {foreignMembers.length === 1
                  ? `${memberName(foreignMembers[0])} wird bisher in einem anderen Haushalt geführt.`
                  : `${foreignMembers.map(memberName).join(", ")} werden bisher in anderen Haushalten geführt.`}{" "}
                Beim Speichern {foreignMembers.length === 1 ? "wird diese Person" : "werden diese Personen"} in
                diesen Haushalt übernommen. Wenn nur die Beziehung präzisiert werden soll, die betroffene Person
                als „kein Haushaltsmitglied" markieren.
              </div>
            )}

            {umembers.map((u, i) => renderMemberCard(u, i))}

            <div className={s.addBox}>
              <span className={s.addTitle}>
                <Add20Regular style={{ fontSize: 18 }} /> Möchtest du einen Kontakt zum Haushalt hinzufügen?
              </span>
              <ContactRefField wantType="Person" personOnly idBase="hh-add" value={addDraft} onChange={setAddDraft} />
              <button className={mergeClasses(s.addBtn, !addValid && s.addBtnDisabled)} disabled={!addValid} onClick={addMember}>
                + Zum Haushalt hinzufügen
              </button>
            </div>

            {renderOverviewTable()}

            {renderPersonConnections()}
            {renderFirmaConnections()}
            {renderKundenverbindung()}

            <div className={s.saveBar}>
              <button
                className={mergeClasses(s.saveBtn, (saving || saveDone || miuFirmaMissing) && s.saveBtnDisabled)}
                disabled={saving || saveDone || miuFirmaMissing}
                onClick={onSave}
              >
                {saving ? "Speichern…" : "Haushalt speichern"}
              </button>
              {saveDone && <span className={s.okText}>Gespeichert ✓</span>}
              {miuFirmaMissing && <span className={s.errText}>Bei „Mensch im Unternehmen = Ja" bitte je Kontakt eine Firma auswählen.</span>}
              {saveError && <span className={s.errText}>{saveError}</span>}
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Listen-Ansicht ────────────────────────────────────────────────────────
  return (
    <div className={s.root}>
      <button className={s.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={s.bar}>
        <div className={s.titleRow}>
          <span className={s.titleIcon}>
            <Home20Regular style={{ fontSize: 20 }} />
          </span>
          <h1 className={s.title}>Haushalte bereinigen</h1>
        </div>
        <span className={s.subtitle}>
          Privatpersonen einer Anschrift zu Haushalten zusammenführen, Rolle im Haushalt vergeben und die
          Beziehungen festlegen.
        </span>
      </div>

      <div className={s.filters}>
        {/* Kandidaten-Quelle */}
        <div className={s.segWrap}>
          <button className={mergeClasses(s.segBtn, source === "addr" && s.segActive)} onClick={() => { setSource("addr"); setShowDismissed(false); }}>
            Anschrift
          </button>
          <button className={mergeClasses(s.segBtn, source === "relation" && s.segActive)} onClick={() => { setSource("relation"); setShowDismissed(false); }}>
            Unklare Beziehung
          </button>
          <button className={mergeClasses(s.segBtn, source === "name" && s.segActive)} onClick={() => { setSource("name"); setShowDismissed(false); }}>
            Namensgleich, getrennt
          </button>
        </div>
        {source === "addr" && (
          <div className={s.segWrap}>
            <button className={mergeClasses(s.segBtn, status === "open" && s.segActive)} onClick={() => setStatus("open")}>
              Offen
            </button>
            <button className={mergeClasses(s.segBtn, status === "bereinigt" && s.segActive)} onClick={() => setStatus("bereinigt")}>
              Bereinigt
            </button>
          </div>
        )}
        {source === "name" && !showDismissed && (
          <div className={s.segWrap}>
            <button className={mergeClasses(s.segBtn, nameWithRelation && s.segActive)} onClick={() => setNameWithRelation(true)}>
              Mit Beziehung
            </button>
            <button className={mergeClasses(s.segBtn, !nameWithRelation && s.segActive)} onClick={() => setNameWithRelation(false)}>
              Ohne Beziehung
            </button>
          </div>
        )}
        {(source === "name" || source === "relation") && (
          <div className={s.segWrap}>
            <button className={mergeClasses(s.segBtn, !showDismissed && s.segActive)} onClick={() => setShowDismissed(false)}>
              Worklist
            </button>
            <button className={mergeClasses(s.segBtn, showDismissed && s.segActive)} onClick={() => setShowDismissed(true)} title="Ausgeblendete Kandidaten (keine Bereinigung nötig)">
              Ausgeblendete
            </button>
          </div>
        )}
        {teams.length > 0 && (
          <select className={s.select} value={teamId} onChange={(e) => setTeamId(e.target.value)} title="Nach Verantwortlichkeit (BO-Team) filtern">
            <option value="">Alle Verantwortlichen</option>
            <option value="none">Ohne Verantwortlichkeit</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name ?? t.id}
              </option>
            ))}
          </select>
        )}
        <div className={s.searchBox}>
          <Search20Regular style={{ color: BRAND.muted, fontSize: 18 }} />
          <input className={s.searchInput} placeholder="Name oder Anschrift suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className={s.headerCount}>
          {(source === "name" || source === "relation") && showDismissed
            ? `${total} ausgeblendet`
            : source === "relation"
              ? `${total} Cluster zu klären`
              : source === "name"
                ? `${total} Namensgruppen`
                : `${total} ${status === "open" ? "offene" : "bereinigte"} Haushalte`}
        </span>
        {source === "relation" && (
          <span className={s.sourceHint}>
            Ein Cluster bündelt alle Haushalte, die über unklare/verwandte Beziehungen zusammenhängen
            („Verwandt" bzw. außerhalb der Auswahlliste). So erscheint ein verbundenes Paar nur EINMAL —
            im Detail werden alle beteiligten Haushalte gezeigt und die Beziehungen sind direkt schärfbar.
            Sobald ein Label geschärft ist, sinkt die Zahl.
          </span>
        )}
        {source === "name" && (
          <span className={s.sourceHint}>
            Personen gleichen Nachnamens, die in getrennten Haushalten geführt werden.{" "}
            {nameWithRelation
              ? "Gezeigt werden nur Gruppen, zwischen denen bereits eine weitere Beziehung besteht."
              : "Nur Gruppen ganz ohne Beziehung — darunter sind naturgemäß viele Namensgleiche ohne Verwandtschaft. Gruppen mit Beziehung stehen im Reiter nebenan."}
          </span>
        )}
      </div>

      <div id="scrollableDivHaushalte" className={s.scrollArea}>
        {(source === "name" || source === "relation") && showDismissed ? (
          dismissedRows.length === 0 ? (
            <div className={s.empty}>Keine ausgeblendeten Kandidaten.</div>
          ) : (
            <>
              {dismissedRows.map((d) => (
                <div className={s.cardRow} key={d.refKey}>
                  <div className={s.card} style={{ marginBottom: 0, flex: 1, minWidth: 0, cursor: "default" }}>
                    <span className={s.cardIcon}>
                      <Home20Regular style={{ fontSize: 22 }} />
                    </span>
                    <span className={s.cardBody}>
                      <span className={s.cardTitle}>{d.label || d.refKey}</span>
                      <span className={s.cardDesc}>
                        Ausgeblendet
                        {d.dismissedAt ? ` · ${new Date(d.dismissedAt).toLocaleDateString("de-DE")}` : ""}
                      </span>
                    </span>
                  </div>
                  <button
                    className={s.sideBtn}
                    title="Wieder in die Worklist aufnehmen"
                    onClick={() => handleRestore(source === "relation" ? "relation" : "name", d.refKey)}
                  >
                    <ArrowUndo20Regular style={{ fontSize: 18 }} />
                    wieder aufnehmen
                  </button>
                </div>
              ))}
            </>
          )
        ) : source === "relation" ? (
          clusters.length === 0 ? (
            <div className={s.empty}>Keine Cluster mit unklaren Beziehungen.</div>
          ) : (
            <InfiniteScroll
              dataLength={clusters.length}
              next={() => loadList(false)}
              hasMore={hasMore}
              loader={<div className={s.empty}>Lädt…</div>}
              scrollableTarget="scrollableDivHaushalte"
            >
              {clusters.map((r) => (
                <div className={s.cardRow} key={r.clusterId}>
                  <button
                    className={s.card}
                    style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                    // Cluster über die beteiligten Kontakte öffnen — das Detail rekonstruiert
                    // daraus alle verbundenen Haushalte.
                    onClick={() => {
                      setClusterSel(r.contactIds);
                      setClusterDismiss({
                        kind: "relation",
                        refKey: String(r.clusterId),
                        label: r.label,
                      });
                    }}
                  >
                    <span className={s.cardIcon}>
                      <Home20Regular style={{ fontSize: 22 }} />
                    </span>
                    <span className={s.cardBody}>
                      <span className={s.cardTitle}>{r.label}</span>
                      <span className={s.cardDesc}>
                        {r.householdCount} {r.householdCount === 1 ? "Haushalt" : "Haushalte"} verbunden
                      </span>
                    </span>
                    <span className={s.labelChip} title="Unklare Beziehungen im Cluster">
                      {r.unclearCount} unklar{r.unclearCount === 1 ? "e" : "e"}
                    </span>
                    {r.verantwortlich && (
                      <span className={s.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                        <PersonBoard20Regular style={{ fontSize: 14 }} />
                        {r.verantwortlich}
                      </span>
                    )}
                    <span className={s.countPill}>
                      {r.householdCount} {r.householdCount === 1 ? "Haushalt" : "Haushalte"}
                    </span>
                    <ChevronRight20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
                  </button>
                  <button
                    className={s.sideBtn}
                    title="Keine Bereinigung nötig – ausblenden"
                    onClick={() => handleDismiss("relation", String(r.clusterId), r.label)}
                  >
                    <CheckmarkCircle20Regular style={{ fontSize: 18 }} />
                    keine Bereinigung
                  </button>
                </div>
              ))}
            </InfiniteScroll>
          )
        ) : source === "name" ? (
          nameRows.length === 0 ? (
            <div className={s.empty}>
              Keine namensgleichen Personen in getrennten Haushalten
              {nameWithRelation ? " mit bestehender Beziehung." : "."}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={nameRows.length}
              next={() => loadList(false)}
              hasMore={hasMore}
              loader={<div className={s.empty}>Lädt…</div>}
              scrollableTarget="scrollableDivHaushalte"
            >
              {nameRows.map((r) => (
                <div className={s.cardRow} key={r.nameKey}>
                  <button
                    className={s.card}
                    style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                    // Gleiche Cluster-Detail-Ansicht wie „Unklare Beziehung": alle
                    // namensgleichen Personen + ihre Haushalte, Beziehungen direkt bearbeitbar.
                    onClick={() => {
                      setClusterSel(r.members.map((m) => m.contactId));
                      setClusterDismiss({
                        kind: "name",
                        refKey: r.nameKey,
                        label: r.lastname,
                      });
                    }}
                    disabled={r.members.length === 0}
                  >
                    <span className={s.cardIcon}>
                      <Home20Regular style={{ fontSize: 22 }} />
                    </span>
                    <span className={s.cardBody}>
                      <span className={s.cardTitle}>{r.lastname}</span>
                      <span className={s.cardDesc}>{memberSummary(r)}</span>
                    </span>
                    {relationSummary(r) && (
                      <span className={s.labelChip} title="Bestehende Beziehungen in dieser Gruppe">
                        {relationSummary(r)}
                      </span>
                    )}
                    {r.verantwortlich && (
                      <span className={s.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                        <PersonBoard20Regular style={{ fontSize: 14 }} />
                        {r.verantwortlich}
                      </span>
                    )}
                    <span className={s.countPill}>{r.memberCount} Personen</span>
                    <ChevronRight20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
                  </button>
                  <button
                    className={s.sideBtn}
                    title="Keine Bereinigung nötig – ausblenden"
                    onClick={() => handleDismiss("name", r.nameKey, r.lastname)}
                  >
                    <CheckmarkCircle20Regular style={{ fontSize: 18 }} />
                    keine Bereinigung
                  </button>
                </div>
              ))}
            </InfiniteScroll>
          )
        ) : rows.length === 0 ? (
          <div className={s.empty}>Keine {status === "open" ? "offenen" : "bereinigten"} Haushalte.</div>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={() => loadList(false)}
            hasMore={hasMore}
            loader={<div className={s.empty}>Lädt…</div>}
            scrollableTarget="scrollableDivHaushalte"
          >
            {rows.map((r) => (
              <button key={`${r.addrKey}|${r.nameKey}`} className={s.card} onClick={() => openDetail(r.addrKey, r.nameKey)}>
                <span className={s.cardIcon}>
                  <Home20Regular style={{ fontSize: 22 }} />
                </span>
                <span className={s.cardBody}>
                  <span className={s.cardTitle}>{(r.names ?? []).join(" · ") || "Haushalt"}</span>
                  <span className={s.cardDesc}>{addrLine(r)}</span>
                </span>
                {r.verantwortlich && (
                  <span className={s.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                    <PersonBoard20Regular style={{ fontSize: 14 }} />
                    {r.verantwortlich}
                  </span>
                )}
                <span className={s.countPill}>{r.memberCount} Personen</span>
                <ChevronRight20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
              </button>
            ))}
          </InfiniteScroll>
        )}
      </div>
      {undo && (
        <div className={s.undoBar}>
          <span>„{undo.label || undo.refKey}" ausgeblendet</span>
          <button className={s.undoBtn} onClick={handleUndo}>
            Rückgängig
          </button>
        </div>
      )}
    </div>
  );
};

export default HaushalteCleanupPage;
